import { QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { RouteErrorBoundary as SharedRouteErrorBoundary } from '@/components/shared/route-error-boundary';
import { useLocale } from '@/hooks/use-locale';
import { queryClient } from '@/lib/query-client';

function RootLayout() {
  const { locale, direction } = useLocale();

  // Keeps <html lang>/<html dir> in step with the active locale for the whole
  // session, including after a hot reload.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      storageKey="app_theme"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster position={direction === 'rtl' ? 'top-left' : 'top-right'} dir={direction} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * The last stop for an error that escaped every route (rule CR1/CR2: the
 * `_app` layout and each top-level feature route get their own instance of
 * `RouteErrorBoundary` too, scoped to a re-mount rather than this one's full
 * reload - see that component for why). `min-h-screen` here is this
 * boundary's own choice as the outermost, full-page instance; a nested one
 * passes a shorter `className` so it does not force full-viewport height
 * inside a smaller content area (rule CR2's "leaves the navigation sidebar
 * ... intact").
 */
function RootErrorBoundary({ error }: { error: unknown }) {
  return <SharedRouteErrorBoundary error={error} boundary="root" className="min-h-screen" />;
}

/**
 * The router itself could not match a route for this URL - a different
 * mechanism entirely from `RouteErrorBoundary` above (no API call was ever
 * made, so there is no server error and no correlation reference to show,
 * rule E15). It otherwise follows the same standard: a real message and one
 * recovery action, never a bare status code with nothing to do about it.
 */
function NotFoundPage() {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{t('notFound.title')}</p>
      <p className="text-sm text-muted-foreground">{t('notFound.description')}</p>
      <Button asChild>
        <Link to="/">{t('notFound.action')}</Link>
      </Button>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
  notFoundComponent: NotFoundPage,
});
